import axios from '../../axios';
import { useQuery } from '@tanstack/react-query';
import type { TSpaceBase, TError } from '@onesource/schemas';
import { ENTITY } from '../entity';

/**
 * Query key for fetching sub-spaces of a parent space - non-hook version
 */
export const getSubSpacesQueryKey = (parentId: string) => [ENTITY, 'spaces', parentId, 'sub-spaces'];

/**
 * Query key for fetching admin sub-spaces (including archived) - non-hook version
 */
export const getAdminSubSpacesQueryKey = (parentId: string) => [ENTITY, 'admin', 'spaces', parentId, 'sub-spaces'];

/**
 * Query key for fetching sub-spaces of a parent space
 */
export const useGetSubSpacesQueryKey = (parentId: string) => getSubSpacesQueryKey(parentId);

interface UseGetSubSpacesOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch all sub-spaces (Feed, Content, Chat) within a parent space
 * Used to determine which tabs to show in space detail page
 * Only returns active spaces (for members)
 *
 * @param parentId - Parent space ID (UUID)
 * @param options - Query options (enabled, etc.)
 */
export const useGetSubSpaces = (parentId: string, options?: UseGetSubSpacesOptions) => {
  const enabled = options?.enabled ?? true;

  return useQuery<TSpaceBase[], TError>({
    queryKey: useGetSubSpacesQueryKey(parentId),
    queryFn: async () => {
      const res = await axios.get<TSpaceBase[]>(`/community-hub/spaces/${parentId}/sub-spaces`);
      return res.data;
    },
    enabled: !!parentId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch all sub-spaces including archived (for admin dashboard)
 *
 * @param parentId - Parent space ID (UUID)
 * @param options - Query options (enabled, etc.)
 */
export const useGetAdminSubSpaces = (parentId: string, options?: UseGetSubSpacesOptions) => {
  const enabled = options?.enabled ?? true;

  return useQuery<TSpaceBase[], TError>({
    queryKey: getAdminSubSpacesQueryKey(parentId),
    queryFn: async () => {
      const res = await axios.get<TSpaceBase[]>(`/community-hub/admin/spaces/${parentId}/sub-spaces`);
      return res.data;
    },
    enabled: !!parentId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
